import { type FormEvent, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FolderKanban, LogOut, Plus, Ticket } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useProject } from '../hooks/useProject';
import { isNavItemActive, NAV_SECTIONS } from '../navigation/navConfig';
import { routes } from '../utils/navigation';
import { cn } from '../utils/cn';
import { trackerApi } from '../services/devtoolsApi';
import { taskboardApi } from '../services/taskboardApi';
import { IconButton } from './ui/IconButton';
import { Input } from './ui/Input';
import { Select } from './ui/Select';

const BOARD_STORAGE_KEY = 'devtools.selectedBoardId';

type Props = {
  /** When true on mobile, drawer is visible over the canvas. */
  mobileOpen: boolean;
  /** Close drawer after a nav click (mobile). */
  onNavigate?: () => void;
};

/**
 * Left rail (desktop) / off-canvas drawer (mobile): Factory + Team nav, epics, tickets, board switcher.
 * Brand/user/Account live in TopBar; this rail owns project-scoped factory navigation.
 * Hides New epic / New ticket forms when AuthContext.isVisitor (API VISITOR_READ_ONLY is the backstop).
 */
export function Sidebar({ mobileOpen, onNavigate }: Props) {
  const { t } = useTranslation();
  const { logout, user, isVisitor } = useAuth();
  const { projectId } = useProject();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [epicTitle, setEpicTitle] = useState('');
  const [ticketTitle, setTicketTitle] = useState('');
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(BOARD_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Clear board preference when the active factory project changes.
  useEffect(() => {
    setSelectedBoardId(null);
  }, [projectId]);

  // 1. Resolve boardId (ensure from DevTools + optional local preference).
  const boardMeta = useQuery({
    queryKey: ['board-meta', projectId],
    queryFn: () =>
      trackerApi.board(new URLSearchParams({ granularity: 'week' }), projectId),
    enabled: Boolean(projectId),
  });
  const ensuredBoardId = boardMeta.data?.boardId ?? null;
  const taskboardUnavailable = boardMeta.isSuccess && !ensuredBoardId;
  const teamId = user?.team_id;

  useEffect(() => {
    if (ensuredBoardId && !selectedBoardId) {
      setSelectedBoardId(ensuredBoardId);
    }
  }, [ensuredBoardId, selectedBoardId]);

  const boardId = selectedBoardId || ensuredBoardId;

  // Wait for session team_id — tools-api boards/search rejects admin JWTs without it.
  const boards = useQuery({
    queryKey: ['tracker-boards', teamId, projectId],
    queryFn: () => taskboardApi.listTrackerBoards(teamId!),
    enabled: Boolean(ensuredBoardId && teamId && projectId),
  });

  // Same explicit team as boards/search — items/search rejects admin JWTs without it.
  const epics = useQuery({
    queryKey: ['epics', boardId, teamId, projectId],
    queryFn: () => taskboardApi.listEpics(boardId!, teamId!),
    enabled: Boolean(boardId && teamId && projectId),
  });

  // 2. Epic/ticket creates invalidate lists; epic create also navigates into the epic.
  const createEpic = useMutation({
    mutationFn: (title: string) => taskboardApi.createEpic(boardId!, teamId!, title),
    onSuccess: (epic) => {
      setEpicTitle('');
      void queryClient.invalidateQueries({ queryKey: ['epics', boardId] });
      onNavigate?.();
      navigate(routes.epic(epic.item_id, epic.path_slug));
    },
  });

  const createTicket = useMutation({
    mutationFn: (title: string) => taskboardApi.createTicket(boardId!, teamId!, title),
    onSuccess: () => {
      setTicketTitle('');
      void queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });

  const onLogout = () => {
    logout();
    onNavigate?.();
    navigate(routes.login(), { replace: true });
  };

  const onCreateEpic = (e: FormEvent) => {
    e.preventDefault();
    const title = epicTitle.trim();
    // Visitors are read-only — API returns VISITOR_READ_ONLY; hide the form in the rail too.
    if (isVisitor || !title || !boardId || !teamId || createEpic.isPending) return;
    createEpic.mutate(title);
  };

  const onCreateTicket = (e: FormEvent) => {
    e.preventDefault();
    const title = ticketTitle.trim();
    // Same visitor gate as onCreateEpic — form is also omitted from the rail below.
    if (isVisitor || !title || !boardId || !teamId || createTicket.isPending) return;
    createTicket.mutate(title);
  };

  const onSelectBoard = (id: string) => {
    setSelectedBoardId(id);
    try {
      localStorage.setItem(BOARD_STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
    void queryClient.invalidateQueries({ queryKey: ['epics'] });
  };

  // Active: accent-muted fill + 2px accent left border (design system), not full purple wash.
  const linkClass = (active: boolean) =>
    cn(
      'flex items-center gap-3 rounded-md border-l-2 px-3 py-2 text-sm',
      active
        ? 'border-[var(--accent)] bg-[var(--accent-muted)] text-[var(--foreground)]'
        : 'border-transparent text-[var(--muted)] hover:bg-[var(--muted-surface)]',
    );

  // Prefer boards scoped to the active project; fall back to all tracker boards.
  const boardList = (() => {
    const all = boards.data?.boards || [];
    const scoped = all.filter((b) => !b.project_id || b.project_id === projectId);
    return scoped.length ? scoped : all;
  })();

  // 3. Rail / drawer chrome: board switcher, Views, Epics, Tickets, logout.
  return (
    <aside
      id="devtools-sidebar"
      className={cn(
        'fixed top-[var(--shell-topbar)] bottom-0 left-0 z-[1003] flex w-[var(--shell-drawer)] flex-col border-r border-[var(--border)] bg-[var(--card)] transition-transform duration-200 md:z-0 md:w-[var(--shell-rail)] md:translate-x-0',
        mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
      )}
      aria-label={t('nav.sidebar')}
    >
      <div className="border-b border-[var(--border)] p-3">
        {boardList.length > 1 ? (
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)]">
            {t('board.switcher')}
            <Select
              className="mt-1 text-xs"
              value={boardId || ''}
              onChange={(e) => onSelectBoard(e.target.value)}
              aria-label={t('board.switcher')}
            >
              {boardList.map((b) => (
                <option key={b.board_id} value={b.board_id}>
                  {b.title}
                </option>
              ))}
            </Select>
          </label>
        ) : (
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)]">
            {t('shell.factoryNav')}
          </p>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto p-2">
        {NAV_SECTIONS.map((section) => (
          <div key={section.id} className="mb-4">
            <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)]">
              {t(section.labelKey)}
            </div>
            <ul className="flex list-none flex-col gap-1 p-0">
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isNavItemActive(
                  location.pathname,
                  item.path,
                  item.matchPrefix,
                );
                return (
                  <li key={item.id}>
                    <NavLink
                      to={item.path}
                      end={item.path === '/'}
                      aria-current={active ? 'page' : undefined}
                      className={linkClass(active)}
                      onClick={() => onNavigate?.()}
                    >
                      <Icon size={18} strokeWidth={1.75} aria-hidden />
                      <span>{t(item.labelKey)}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="mb-2">
          <div className="flex items-center gap-2 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)]">
            <FolderKanban size={12} aria-hidden />
            {t('nav.epics')}
          </div>
          {taskboardUnavailable ? (
            <p className="px-3 text-xs text-[var(--muted)]" role="status">
              {t('taskboard.unavailable')}
            </p>
          ) : null}
          {/* Mutation errors win: the user just submitted. List errors cover a failed epic fetch. */}
          {epics.isError || createEpic.isError || createTicket.isError ? (
            <p className="px-3 text-xs text-[var(--beatly-warning)]" role="alert">
              {(createEpic.error as Error | null)?.message
                || (createTicket.error as Error | null)?.message
                || (epics.error as Error | null)?.message
                || t('common.error')}
            </p>
          ) : null}
          <ul className="flex list-none flex-col gap-1 p-0">
            {(epics.data?.items || []).map((epic) => {
              const path = routes.epic(epic.item_id, epic.path_slug);
              const active = location.pathname === path;
              return (
                <li key={epic.item_id}>
                  <NavLink
                    to={path}
                    aria-current={active ? 'page' : undefined}
                    className={linkClass(active)}
                    title={epic.title}
                    onClick={() => onNavigate?.()}
                  >
                    <span className="truncate">{epic.title}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
          {/* Create CTAs mutate the board — omit for kitchen visitor JWTs. */}
          {!isVisitor ? (
            <form onSubmit={onCreateEpic} className="mt-2 flex gap-1 px-1">
              <Input
                className="min-w-0 flex-1 px-2 py-1.5 text-xs"
                value={epicTitle}
                onChange={(e) => setEpicTitle(e.target.value)}
                placeholder={t('epics.newPlaceholder')}
                aria-label={t('epics.newPlaceholder')}
                disabled={!boardId || !teamId || createEpic.isPending}
              />
              <IconButton
                type="submit"
                label={t('epics.create')}
                disabled={!boardId || !teamId || !epicTitle.trim() || createEpic.isPending}
              >
                <Plus size={14} aria-hidden />
              </IconButton>
            </form>
          ) : null}
        </div>

        <div className="mb-2">
          <div className="flex items-center gap-2 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)]">
            <Ticket size={12} aria-hidden />
            {t('tickets.section')}
          </div>
          {/* Same visitor omit as New epic — submit handlers also early-return. */}
          {!isVisitor ? (
            <form onSubmit={onCreateTicket} className="mt-1 flex gap-1 px-1">
              <Input
                className="min-w-0 flex-1 px-2 py-1.5 text-xs"
                value={ticketTitle}
                onChange={(e) => setTicketTitle(e.target.value)}
                placeholder={t('tickets.newPlaceholder')}
                aria-label={t('tickets.newPlaceholder')}
                disabled={!boardId || !teamId || createTicket.isPending}
              />
              <IconButton
                type="submit"
                label={t('tickets.create')}
                disabled={!boardId || !teamId || !ticketTitle.trim() || createTicket.isPending}
              >
                <Plus size={14} aria-hidden />
              </IconButton>
            </form>
          ) : null}
        </div>
      </nav>

      <div className="mt-auto border-t border-[var(--border)] p-2">
        <button
          type="button"
          onClick={onLogout}
          aria-label={t('nav.logout')}
          className="flex w-full items-center gap-3 rounded-full px-3 py-2 text-sm text-[var(--muted)] hover:bg-[var(--muted-surface)]"
        >
          <LogOut size={18} strokeWidth={1.75} aria-hidden />
          <span>{t('nav.logout')}</span>
        </button>
      </div>
    </aside>
  );
}
